import React from 'react';
import styled from 'styled-components';
import { Checkbox, Collapse } from 'antd';
import escapeRegExp from 'lodash/escapeRegExp';
import { DownOutlined } from '@ant-design/icons';
import { COLOR, FONT_SIZE, RADIUS, SPACING } from '../../../constants/theme';

const GroupTitle = styled.h2`
  margin: 0 0 10px 4px;
  font-size: ${FONT_SIZE.sectionTitle};
  font-weight: 700;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: ${COLOR.textSecondary};
`;

const GroupHint = styled.p`
  margin: 0 0 ${SPACING.md} 4px;
  font-size: ${FONT_SIZE.body};
  color: ${COLOR.textSecondary};
`;

const CollapseContainer = styled(Collapse)`
  background: transparent;
  border: none;

  > .ant-collapse-item {
    margin-bottom: ${SPACING.sm};
    background: ${COLOR.surface};
    border: 1px solid ${COLOR.border};
    border-radius: ${RADIUS.card};
    overflow: hidden;
  }

  > .ant-collapse-item:last-child {
    margin-bottom: 0;
    border-radius: ${RADIUS.card};
  }

  > .ant-collapse-item.is-agreed {
    background: ${COLOR.primaryLight};
    border-color: ${COLOR.primaryBorderStrong};
  }

  > .ant-collapse-item > .ant-collapse-header {
    align-items: center;
    /* The right padding keeps a long title clear of the expand arrow,
       which antd positions absolutely at the header's right. */
    padding: 13px 44px 13px ${SPACING.xl};
    font-size: ${FONT_SIZE.value};
    font-weight: 500;
    color: ${COLOR.text};
  }

  > .ant-collapse-item > .ant-collapse-content {
    background: transparent;
    border-top: 1px solid ${COLOR.border};
  }

  .ant-collapse-content-box {
    padding: 14px ${SPACING.xl};
  }
`;

const HeaderRow = styled.span`
  display: flex;
  align-items: center;
  gap: ${SPACING.md};

  .ant-checkbox-wrapper {
    flex: none;
  }

  .ant-checkbox-inner {
    width: 19px;
    height: 19px;
  }
`;

const HeaderTitle = styled.span`
  flex: 1;
  min-width: 0;
`;

const Body = styled.div`
  font-size: ${FONT_SIZE.body};
  line-height: 1.6;
  color: ${COLOR.textSecondary};

  p {
    margin: 0 0 10px;
  }

  p:last-child {
    margin-bottom: 0;
  }

  b {
    font-weight: 500;
    color: ${COLOR.text};
  }
`;

const ErrorMessage = styled.div`
  margin-top: ${SPACING.md};
  padding: 10px 14px;
  border: 1px solid ${COLOR.errorBorder};
  border-radius: ${RADIUS.md};
  background: ${COLOR.errorBackground};
  font-size: ${FONT_SIZE.body};
  color: ${COLOR.errorText};
`;

const renderTextWithEmphasis = (text, emphasis) => {
  if (!emphasis?.length) return text;

  const emphasisPattern = new RegExp(
    `(${emphasis.map(escapeRegExp).join('|')})`,
    'g'
  );

  return String(text)
    .split(emphasisPattern)
    .map((part, index) =>
      emphasis.includes(part) ? <b key={index}>{part}</b> : part
    );
};

const renderParagraph = (paragraph, index) => {
  if (typeof paragraph === 'string') {
    return <p key={index}>{paragraph}</p>;
  }

  const { lead, text, emphasis } = paragraph;

  return (
    <p key={index}>
      {lead && <b>{lead}</b>}
      {lead ? ' ' : ''}
      {renderTextWithEmphasis(text, emphasis)}
    </p>
  );
};

const renderBody = (body) => {
  if (Array.isArray(body)) {
    return body.map(renderParagraph);
  }

  return body;
};

const TermsAccordion = ({
  groupTitle,
  groupHint,
  sections = [],
  agreements = {},
  onAgreementChange,
  defaultOpenKeys = [],
  errorMessage,
  showError,
}) => {
  if (!sections.length) return null;

  const handleCheckboxChange = (sectionKey) => (event) => {
    onAgreementChange(sectionKey, event.target.checked);
  };

  const stopHeaderToggle = (event) => {
    event.stopPropagation();
  };

  return (
    <section>
      {groupTitle && <GroupTitle>{groupTitle}</GroupTitle>}
      {groupHint && <GroupHint>{groupHint}</GroupHint>}
      <CollapseContainer
        defaultActiveKey={defaultOpenKeys}
        expandIconPosition="right"
        expandIcon={({ isActive }) => (
          <DownOutlined
            rotate={isActive ? 180 : 0}
            style={{ color: COLOR.textSecondary }}
          />
        )}
      >
        {sections.map(({ key, title, body, agreeLabel }) => (
          <Collapse.Panel
            key={key}
            className={agreements[key] ? 'is-agreed' : ''}
            header={
              <HeaderRow>
                <Checkbox
                  checked={Boolean(agreements[key])}
                  onChange={handleCheckboxChange(key)}
                  onClick={stopHeaderToggle}
                  aria-label={agreeLabel || `I have read and agree to ${title}`}
                />
                <HeaderTitle>{title}</HeaderTitle>
              </HeaderRow>
            }
          >
            <Body>{renderBody(body)}</Body>
          </Collapse.Panel>
        ))}
      </CollapseContainer>
      {showError && errorMessage && <ErrorMessage>{errorMessage}</ErrorMessage>}
    </section>
  );
};

// Wraps a page's terms block; spaced like a Card so booking and payment match.
export const TermsSection = styled.div`
  margin-bottom: ${SPACING.lg};
`;

export const areAllSectionsAgreed = (sections = [], agreements = {}) =>
  sections.length > 0 && sections.every(({ key }) => Boolean(agreements[key]));

export default TermsAccordion;
